import { Cell, Grid } from 'bold-ui'
import { DateField, MunicipioSelectField, RadioGroupField, TextField } from 'components/form'
import { LocalNascimentoRadioGroupField } from 'components/form/field/LocalNascimentoRadioGroupField'
import { AldeiaSelectField } from 'components/form/field/select/AldeiaSelectField'
import { LocalOcorrenciaSelectField } from 'components/form/field/select/LocalOcorrenciaSelectField'
import { PaisSelectField } from 'components/form/field/select/PaisSelectField'
import { NacionalidadeEnum, TipoLocalNascimentoEnum } from 'graphql/types.generated'
import { Fragment, useMemo } from 'react'
import { calculateAge } from 'util/date/calculateAge'
import { toDate } from 'util/date/formatDate'
import { onlyNumbersLetters } from 'util/mask'
import { MetaRoot } from 'util/metaPath'

import { CidadaoAldeadoFormModel, NacionalidadeFormModel } from '../types/model-cidadao'

interface NacionalidadeFieldGroupProps {
  meta: MetaRoot<NacionalidadeFormModel>
  maxDate: Date
  values: NacionalidadeFormModel
  isCidadaoAldeado: boolean
  dataNascimento: string
  valuesIndigena: CidadaoAldeadoFormModel
  metaIndigena: MetaRoot<CidadaoAldeadoFormModel>
}

export const NacionalidadeFieldGroup = (props: NacionalidadeFieldGroupProps) => {
  const { meta, maxDate, values, isCidadaoAldeado, dataNascimento, valuesIndigena, metaIndigena } = props

  const idadeEmAnos = useMemo(() => dataNascimento && calculateAge(dataNascimento).years, [dataNascimento])
  const idadeMenorQueUm = idadeEmAnos !== null && idadeEmAnos < 1

  const isNaturalizado = values?.nacionalidade === NacionalidadeEnum.NATURALIZADA
  const isEstrangeiro = values?.nacionalidade === NacionalidadeEnum.ESTRANGEIRA
  const isBrasileiro = values?.nacionalidade === NacionalidadeEnum.BRASILEIRA

  const dataMinimaEntradaPaisNaturalizacao = toDate(dataNascimento)

  const isTipoLocalNascimentoAldeia = valuesIndigena?.tipoLocalNascimento === TipoLocalNascimentoEnum.ALDEIA
  return (
    <Grid>
      <Cell size={6}>
        <RadioGroupField
          label='Nacionalidade'
          name={meta.nacionalidade}
          options={[
            { value: NacionalidadeEnum.BRASILEIRA, label: 'Brasileira' },
            { value: NacionalidadeEnum.NATURALIZADA, label: 'Naturalizado' },
            { value: NacionalidadeEnum.ESTRANGEIRA, label: 'Estrangeiro' },
          ]}
          required
        />
      </Cell>
      {isBrasileiro && isCidadaoAldeado ? (
        <Cell size={6}>
          <LocalNascimentoRadioGroupField
            name={metaIndigena.tipoLocalNascimento}
            required={isBrasileiro && idadeMenorQueUm}
          />
        </Cell>
      ) : (
        <Cell size={6} />
      )}
      {isTipoLocalNascimentoAldeia && (
        <Fragment>
          <Cell size={6}>
            <AldeiaSelectField
              name={metaIndigena.aldeiaNascimento}
              label='Aldeia de nascimento'
              required={idadeMenorQueUm}
            />
          </Cell>
          <Cell size={6}>
            <LocalOcorrenciaSelectField
              name={metaIndigena.localOcorrencia}
              label='Local de ocorrência do nascimento'
              required={idadeMenorQueUm}
            />
          </Cell>
        </Fragment>
      )}
      <Cell size={6}>
        {isBrasileiro && (!isCidadaoAldeado || !isTipoLocalNascimentoAldeia) && (
          <MunicipioSelectField
            name={meta.municipioNascimento}
            label='Município de nascimento'
            required={!isCidadaoAldeado || idadeMenorQueUm}
          />
        )}
        {isEstrangeiro && (
          <PaisSelectField name={meta.paisNascimento} omiteBrasil={true} label='País de nascimento' required />
        )}
        {isNaturalizado && (
          <TextField
            name={meta.portariaNaturalizacao}
            label='Portaria de naturalização'
            parse={onlyNumbersLetters}
            maxLength={16}
            required
          />
        )}
      </Cell>
      <Cell size={3}>
        {isEstrangeiro && (
          <DateField
            name={meta.dataEntradaPais}
            minDate={dataMinimaEntradaPaisNaturalizacao}
            maxDate={maxDate}
            label='Data de entrada no Brasil'
            required
          />
        )}
        {isNaturalizado && (
          <DateField
            name={meta.dataNaturalizacao}
            minDate={dataMinimaEntradaPaisNaturalizacao}
            maxDate={maxDate}
            label='Data de naturalização'
            required
          />
        )}
      </Cell>
      <Cell size={3} />
    </Grid>
  )
}
